import React from 'react';
import clsx from 'clsx';

import Icon from '@components/icon/Icon';

import VoiceWaveform from './VoiceWaveform';

import useVoiceRecorder from '../../hooks/useVoiceRecorder';
import { formatDuration } from '../../utils/voiceNote';

type Recorder = ReturnType<typeof useVoiceRecorder>;

const RoundButton = ({
  label,
  icon,
  className,
  onClick
}: {
  label: string;
  icon: string;
  className: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className={clsx(
      'flex size-8 shrink-0 items-center justify-center rounded-full',
      className
    )}
  >
    <Icon name={icon} size="medium" />
  </button>
);

// Floating bar shown while a voice note is recording: live waveform, timer,
// discard (✕), pause / resume (■ / mic) and done (✓).
const VoiceRecordingBar: React.FC<{ recorder: Recorder }> = ({ recorder }) => {
  const isRecording = recorder.status === 'recording';

  return (
    <div
      className="flex w-105 max-w-full items-center gap-3 rounded-xl bg-[#1C1C1D] px-4 py-2 shadow-lg"
      role="status"
      aria-label={isRecording ? 'Recording voice note' : 'Recording paused'}
    >
      <VoiceWaveform
        bars={recorder.liveLevels}
        barClassName="bg-grey60"
        activeBarClassName="bg-grey60"
      />
      <span className="flex shrink-0 items-center gap-1.5 text-sm tabular-nums text-white">
        <span
          className={clsx(
            'size-1.5 rounded-full bg-red-500',
            isRecording && 'animate-pulse'
          )}
        />
        {formatDuration(recorder.elapsedSec)}
      </span>
      <RoundButton
        label="Discard recording"
        icon="close-white"
        className="bg-grey80 hover:bg-grey60"
        onClick={recorder.discard}
      />
      <RoundButton
        label={isRecording ? 'Pause recording' : 'Resume recording'}
        icon={isRecording ? 'stop-white' : 'mic-white'}
        className="bg-red-500 hover:bg-red-600"
        onClick={isRecording ? recorder.pause : recorder.resume}
      />
      <RoundButton
        label="Finish recording"
        icon="check-white"
        className="bg-[#00A657] hover:bg-green-700"
        onClick={recorder.stop}
      />
    </div>
  );
};

export default VoiceRecordingBar;
